import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react"
import { Button, Upload, Card, Space, Segmented, Slider } from "antd"
import { UploadOutlined, ReloadOutlined, ZoomInOutlined, ZoomOutOutlined, ExpandOutlined, ScissorOutlined, CopyOutlined, DeleteOutlined } from "@ant-design/icons"
import "./App.css"
import PageLayout from "./PageLayout"
import { hello, logEvent, removeBackgroundAI } from "./endpoints"
import Introduction from "./Introduction"
import { useWindowWidth } from "./useWindowWidth"

// Passport photo specifications
type PassportType = 'cn' | 'us'
const PASSPORT_CONFIG = {
  cn: {
    label: '🇨🇳  Chinese Passport',
    cropRatio: 16 / 11,           // height / width
    hintText: 'Draw crop area (11 × 16 ratio)',
    photoWmm: 33, photoHmm: 48,  // mm
    photoWin: null, photoHin: null,
    cols: 2, rows: 2,
    description: '33 × 48 mm per photo · 4 photos · evenly spaced',
  },
  us: {
    label: '🇺🇸  US Passport',
    cropRatio: 1,                 // 1:1 square
    hintText: 'Draw crop area (1 × 1 ratio)',
    photoWmm: null, photoHmm: null,
    photoWin: 2, photoHin: 2,    // inches
    cols: 1, rows: 2,
    description: '2 × 2 in per photo · 2 photos · stacked vertically',
  },
} as const

interface Rect { x: number; y: number; w: number; h: number }

interface ChannelLevels {
  inputBlack: number
  inputWhite: number
  gamma: number
}
interface AllLevels { r: ChannelLevels; g: ChannelLevels; b: ChannelLevels }
type Channel = 'rgb' | 'r' | 'g' | 'b'
const defaultCh = (): ChannelLevels => ({ inputBlack: 0, inputWhite: 255, gamma: 1.0 })
const MAX_UNDO_HISTORY = 20
function App() {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [imageVersion, setImageVersion] = useState(0)
  const [passportType, setPassportType] = useState<PassportType>('cn')
  const passportConf = PASSPORT_CONFIG[passportType]
  const cropRatio = passportConf.cropRatio
  const isMobile = useWindowWidth() < 768
  const isiOS = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )

  const [levels, setLevels] = useState<AllLevels>({ r: defaultCh(), g: defaultCh(), b: defaultCh() })
  const [channel, setChannel] = useState<Channel>('rgb')

  // ── image canvas ──────────────────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const originalImageRef = useRef<HTMLImageElement | null>(null)
  const uploadedImageRef = useRef<HTMLImageElement | null>(null)

  // ── zoom / pan ────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const viewportRef = useRef<HTMLDivElement>(null)
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, y: 0 })
  const offsetAtPanStart = useRef({ x: 0, y: 0 })

  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.25, 10))
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.25, 0.1))
  const handleResetView = () => { setZoom(1); setOffset({ x: 0, y: 0 }) }

  // ── crop ──────────────────────────────────────────────────────────────────
  const [cropMode, setCropMode] = useState(false)
  const [cropRect, setCropRect] = useState<Rect | null>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  type CropDragKind = "drawing" | "moving" | null
  const cropDragKind = useRef<CropDragKind>(null)
  const cropMouseStart = useRef({ x: 0, y: 0 })
  const cropRectAtStart = useRef<Rect | null>(null)

  // ── copied preview ────────────────────────────────────────────────────────
  const [croppedSrc, setCroppedSrc] = useState<string | null>(null)
  const [photo4x6Src, setPhoto4x6Src] = useState<string | null>(null)
  const [uploadedSrc, setUploadedSrc] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [bgRemovalError, setBgRemovalError] = useState<string | null>(null)
  const [converting, setConverting] = useState(false)
  const [backgroundRemoved, setBackgroundRemoved] = useState(false)
  const [magicEraserMode, setMagicEraserMode] = useState(false)
  const [magicTolerance, setMagicTolerance] = useState(36)
  const [magicSampleSize, setMagicSampleSize] = useState(9)
  const [regularEraserMode, setRegularEraserMode] = useState(false)
  const [regularEraserSize, setRegularEraserSize] = useState(32)
  const [autoRemovingBg, setAutoRemovingBg] = useState(false)
  const [overlayLayoutVersion, setOverlayLayoutVersion] = useState(0)
  const [regularCursorPos, setRegularCursorPos] = useState<{ x: number; y: number } | null>(null)
  const [magicCursorPos, setMagicCursorPos] = useState<{ x: number; y: number } | null>(null)
  const [helloMessage, setHelloMessage] = useState("Loading hello message...")
  const editHistoryRef = useRef<string[]>([])
  const regularErasingRef = useRef(false)
  const regularStrokeChangedRef = useRef(false)
  const regularStrokeUndoPushedRef = useRef(false)

  // ── histogram ─────────────────────────────────────────────────────────────
  const [histData, setHistData] = useState<{ r: number[]; g: number[]; b: number[] }>({
    r: new Array(256).fill(0), g: new Array(256).fill(0), b: new Array(256).fill(0),
  })
  const histRef = useRef<HTMLCanvasElement>(null)
  const inputTrackRef = useRef<HTMLDivElement>(null)

  // ── helpers ───────────────────────────────────────────────────────────────
  const viewportPos = (e: React.MouseEvent) => {
    const vp = viewportRef.current
    if (!vp) return { x: e.clientX, y: e.clientY }
    const r = vp.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  // Converts a mouse event to canvas-image pixel coordinates.
  // cropRect is stored in this space so it stays stable when the layout
  // shrinks the viewport (e.g. when the cropped preview appears beside it).
  const canvasCropPos = (e: React.MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current
    const viewport = viewportRef.current
    if (!canvas || !viewport) return { x: 0, y: 0 }
    const canvasBCR = canvas.getBoundingClientRect()
    const vpBCR = viewport.getBoundingClientRect()
    const scaleX = canvas.width / canvasBCR.width
    const scaleY = canvas.height / canvasBCR.height
    const vx = e.clientX - vpBCR.left
    const vy = e.clientY - vpBCR.top
    return {
      x: (vx - (canvasBCR.left - vpBCR.left)) * scaleX,
      y: (vy - (canvasBCR.top - vpBCR.top)) * scaleY,
    }
  }

  const insideRect = (px: number, py: number, r: Rect) =>
    px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h

  // Clamps a rect (in canvas-image pixel coordinates) inside the canvas bounds.
  const clampRect = (r: Rect): Rect => {
    const canvas = canvasRef.current
    const cw = canvas ? canvas.width : 9999
    const ch = canvas ? canvas.height : 9999
    const x = Math.max(0, Math.min(r.x, cw - r.w))
    const y = Math.max(0, Math.min(r.y, ch - r.h))
    return { x, y, w: r.w, h: r.h }
  }

  // ── unified mouse handlers ─────────────────────────────────────────────────
  const handleMagicEraserClick = (e: React.MouseEvent) => {
    const canvas = canvasRef.current
    const viewport = viewportRef.current
    if (!canvas || !viewport) return

    const pos = viewportPos(e)
    const canvasBCR = canvas.getBoundingClientRect()
    const vpBCR = viewport.getBoundingClientRect()
    const canvasLeft = canvasBCR.left - vpBCR.left
    const canvasTop = canvasBCR.top - vpBCR.top
    const scaleX = canvas.width / canvasBCR.width
    const scaleY = canvas.height / canvasBCR.height
    const sx = Math.round((pos.x - canvasLeft) * scaleX)
    const sy = Math.round((pos.y - canvasTop) * scaleY)

    if (sx < 0 || sy < 0 || sx >= canvas.width || sy >= canvas.height) return

    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    if (!ctx) return
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    const w = canvas.width
    const h = canvas.height
    const idxOf = (x: number, y: number) => y * w + x
    const seedIdx = idxOf(sx, sy)
    const seedI = seedIdx * 4
    if (data[seedI + 3] === 0) return

    const sampleRadius = Math.max(0, Math.floor(magicSampleSize / 2))
    let sumR = 0
    let sumG = 0
    let sumB = 0
    let sampleCount = 0
    for (let yy = Math.max(0, sy - sampleRadius); yy <= Math.min(h - 1, sy + sampleRadius); yy += 1) {
      for (let xx = Math.max(0, sx - sampleRadius); xx <= Math.min(w - 1, sx + sampleRadius); xx += 1) {
        const i = idxOf(xx, yy) * 4
        if (data[i + 3] === 0) continue
        sumR += data[i]
        sumG += data[i + 1]
        sumB += data[i + 2]
        sampleCount += 1
      }
    }
    const seedR = sampleCount > 0 ? Math.round(sumR / sampleCount) : data[seedI]
    const seedG = sampleCount > 0 ? Math.round(sumG / sampleCount) : data[seedI + 1]
    const seedB = sampleCount > 0 ? Math.round(sumB / sampleCount) : data[seedI + 2]
    const tol = magicTolerance
    const tolSq = tol * tol

    const visited = new Uint8Array(w * h)
    const qx = new Int32Array(w * h)
    const qy = new Int32Array(w * h)
    let head = 0
    let tail = 0
    for (let yy = Math.max(0, sy - sampleRadius); yy <= Math.min(h - 1, sy + sampleRadius); yy += 1) {
      for (let xx = Math.max(0, sx - sampleRadius); xx <= Math.min(w - 1, sx + sampleRadius); xx += 1) {
        const nIdx = idxOf(xx, yy)
        if (visited[nIdx]) continue
        visited[nIdx] = 1
        qx[tail] = xx
        qy[tail] = yy
        tail += 1
      }
    }

    let removed = 0
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]] as const
    while (head < tail) {
      const x = qx[head]
      const y = qy[head]
      head += 1
      const pIdx = idxOf(x, y)
      const i = pIdx * 4
      if (data[i + 3] === 0) continue

      const dr = data[i] - seedR
      const dg = data[i + 1] - seedG
      const db = data[i + 2] - seedB
      const d2 = dr * dr + dg * dg + db * db
      if (d2 > tolSq) continue

      const alreadyWhite = data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255 && data[i + 3] === 255
      if (!alreadyWhite) {
        data[i] = 255
        data[i + 1] = 255
        data[i + 2] = 255
        data[i + 3] = 255
        removed += 1
      }

      for (const [dx, dy] of dirs) {
        const nx = x + dx
        const ny = y + dy
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue
        const nIdx = idxOf(nx, ny)
        if (visited[nIdx]) continue
        visited[nIdx] = 1
        qx[tail] = nx
        qy[tail] = ny
        tail += 1
      }
    }

    if (removed === 0) {
      setBgRemovalError("No connected pixels matched this color. Increase Magic Eraser tolerance.")
      return
    }

    if (imageSrc) pushUndoState(imageSrc)
    ctx.putImageData(imageData, 0, 0)
    const processedSrc = canvas.toDataURL("image/png")
    setWorkingImageFromDataUrl(processedSrc)
    setBgRemovalError(null)
    logEvent({
      event: "magic_erase_click",
      removedPixels: removed,
      tolerance: magicTolerance,
      sampleSize: magicSampleSize,
      userAgent: navigator.userAgent,
    })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    if (regularEraserMode) {
      const canvas = canvasRef.current
      const viewport = viewportRef.current
      if (!canvas || !viewport) return
      const pos = viewportPos(e)
      setRegularCursorPos(pos)

      const ctx = canvas.getContext("2d", { willReadFrequently: true })
      if (!ctx) return
      const canvasBCR = canvas.getBoundingClientRect()
      const vpBCR = viewport.getBoundingClientRect()
      const canvasLeft = canvasBCR.left - vpBCR.left
      const canvasTop = canvasBCR.top - vpBCR.top
      const scaleX = canvas.width / canvasBCR.width
      const scaleY = canvas.height / canvasBCR.height
      const cx = Math.round((pos.x - canvasLeft) * scaleX)
      const cy = Math.round((pos.y - canvasTop) * scaleY)
      const r = Math.max(1, Math.floor(regularEraserSize / 2))
      if (cx < 0 || cy < 0 || cx >= canvas.width || cy >= canvas.height) return

      regularErasingRef.current = true
      regularStrokeChangedRef.current = false
      regularStrokeUndoPushedRef.current = false

      const minX = Math.max(0, cx - r)
      const minY = Math.max(0, cy - r)
      const maxX = Math.min(canvas.width - 1, cx + r)
      const maxY = Math.min(canvas.height - 1, cy + r)
      const w = maxX - minX + 1
      const h = maxY - minY + 1
      const imageData = ctx.getImageData(minX, minY, w, h)
      const data = imageData.data
      const r2 = r * r
      let changed = false
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const dx = minX + x - cx
          const dy = minY + y - cy
          if (dx * dx + dy * dy > r2) continue
          const i = (y * w + x) * 4
          const alreadyWhite = data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255 && data[i + 3] === 255
          if (!alreadyWhite) {
            data[i] = 255
            data[i + 1] = 255
            data[i + 2] = 255
            data[i + 3] = 255
            changed = true
          }
        }
      }
      if (changed) {
        if (!regularStrokeUndoPushedRef.current && imageSrc) {
          pushUndoState(imageSrc)
          regularStrokeUndoPushedRef.current = true
        }
        ctx.putImageData(imageData, minX, minY)
        regularStrokeChangedRef.current = true
      }
      return
    }
    if (magicEraserMode) {
      setMagicCursorPos(viewportPos(e))
      handleMagicEraserClick(e)
      return
    }
    if (cropMode) {
      const pos = canvasCropPos(e)
      if (cropRect && insideRect(pos.x, pos.y, cropRect)) {
        cropDragKind.current = "moving"
        cropMouseStart.current = pos
        cropRectAtStart.current = { ...cropRect }
      } else {
        cropDragKind.current = "drawing"
        cropMouseStart.current = pos
        setCropRect(null)
      }
    } else {
      isPanning.current = true
      panStart.current = { x: e.clientX, y: e.clientY }
      offsetAtPanStart.current = { ...offset }
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (regularEraserMode) {
      const canvas = canvasRef.current
      const viewport = viewportRef.current
      if (!canvas || !viewport) return
      const pos = viewportPos(e)
      setRegularCursorPos(pos)
      if (!regularErasingRef.current) return

      const ctx = canvas.getContext("2d", { willReadFrequently: true })
      if (!ctx) return
      const canvasBCR = canvas.getBoundingClientRect()
      const vpBCR = viewport.getBoundingClientRect()
      const canvasLeft = canvasBCR.left - vpBCR.left
      const canvasTop = canvasBCR.top - vpBCR.top
      const scaleX = canvas.width / canvasBCR.width
      const scaleY = canvas.height / canvasBCR.height
      const cx = Math.round((pos.x - canvasLeft) * scaleX)
      const cy = Math.round((pos.y - canvasTop) * scaleY)
      const r = Math.max(1, Math.floor(regularEraserSize / 2))
      if (cx < 0 || cy < 0 || cx >= canvas.width || cy >= canvas.height) return

      const minX = Math.max(0, cx - r)
      const minY = Math.max(0, cy - r)
      const maxX = Math.min(canvas.width - 1, cx + r)
      const maxY = Math.min(canvas.height - 1, cy + r)
      const w = maxX - minX + 1
      const h = maxY - minY + 1
      const imageData = ctx.getImageData(minX, minY, w, h)
      const data = imageData.data
      const r2 = r * r
      let changed = false
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const dx = minX + x - cx
          const dy = minY + y - cy
          if (dx * dx + dy * dy > r2) continue
          const i = (y * w + x) * 4
          const alreadyWhite = data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255 && data[i + 3] === 255
          if (!alreadyWhite) {
            data[i] = 255
            data[i + 1] = 255
            data[i + 2] = 255
            data[i + 3] = 255
            changed = true
          }
        }
      }
      if (changed) {
        if (!regularStrokeUndoPushedRef.current && imageSrc) {
          pushUndoState(imageSrc)
          regularStrokeUndoPushedRef.current = true
        }
        ctx.putImageData(imageData, minX, minY)
        regularStrokeChangedRef.current = true
      }
      return
    }
    if (magicEraserMode) {
      setMagicCursorPos(viewportPos(e))
      return
    }
    if (cropMode) {
      const pos = canvasCropPos(e)
      if (cropDragKind.current === "drawing") {
        const dx = pos.x - cropMouseStart.current.x
        const dy = pos.y - cropMouseStart.current.y
        let w = Math.abs(dx)
        let h = w * cropRatio
        if (Math.abs(dy) > Math.abs(dx)) { h = Math.abs(dy); w = h / cropRatio }
        const x = dx >= 0 ? cropMouseStart.current.x : cropMouseStart.current.x - w
        const y = dy >= 0 ? cropMouseStart.current.y : cropMouseStart.current.y - h
        setCropRect(clampRect({ x, y, w, h }))
      } else if (cropDragKind.current === "moving" && cropRectAtStart.current) {
        const dx = pos.x - cropMouseStart.current.x
        const dy = pos.y - cropMouseStart.current.y
        const r = cropRectAtStart.current
        setCropRect(clampRect({ x: r.x + dx, y: r.y + dy, w: r.w, h: r.h }))
      }
    } else {
      if (!isPanning.current) return
      const dx = e.clientX - panStart.current.x
      const dy = e.clientY - panStart.current.y
      setOffset({ x: offsetAtPanStart.current.x + dx, y: offsetAtPanStart.current.y + dy })
    }
  }

  const finalizeRegularStroke = () => {
    if (!regularErasingRef.current) return
    regularErasingRef.current = false
    if (!regularStrokeChangedRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const processedSrc = canvas.toDataURL("image/png")
    setWorkingImageFromDataUrl(processedSrc)
    regularStrokeChangedRef.current = false
  }

  const handleMouseUp = () => {
    cropDragKind.current = null
    isPanning.current = false
    finalizeRegularStroke()
  }
  const handleMouseLeave = () => {
    cropDragKind.current = null
    isPanning.current = false
    finalizeRegularStroke()
    setMagicCursorPos(null)
    setRegularCursorPos(null)
  }

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const canvas = canvasRef.current
    if (!viewport || !canvas || typeof ResizeObserver === "undefined") return

    let frameId = 0
    const bumpOverlayLayoutVersion = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        setOverlayLayoutVersion((v) => v + 1)
      })
    }

    const observer = new ResizeObserver(() => {
      bumpOverlayLayoutVersion()
    })

    observer.observe(viewport)
    observer.observe(canvas)
    bumpOverlayLayoutVersion()

    return () => {
      cancelAnimationFrame(frameId)
      observer.disconnect()
    }
  }, [imageSrc, croppedSrc])

  // ── crop overlay redraw ───────────────────────────────────────────────────
  // useLayoutEffect fires BEFORE the browser paints, so getBoundingClientRect()
  // always sees the current (post-layout-shift) canvas dimensions. Using plain
  // useEffect would let the browser paint one frame with a stale (stretched)
  // overlay buffer whenever the viewport width changes (e.g. when the cropped
  // preview panel appears beside the source image).
  useLayoutEffect(() => {
    const overlay = overlayRef.current
    const viewport = viewportRef.current
    if (!overlay || !viewport) return
    const vw = viewport.clientWidth
    const vh = viewport.clientHeight
    overlay.width = vw
    overlay.height = vh
    const ctx = overlay.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, vw, vh)
    if (cropMode) {
      if (cropRect) {
        // cropRect is in canvas-image pixel coords; convert to viewport-display
        // coords so we can draw correctly on the overlay canvas.
        const canvas = canvasRef.current
        if (!canvas) return
        const canvasBCR = canvas.getBoundingClientRect()
        const vpBCR = viewport.getBoundingClientRect()
        const canvasLeft = canvasBCR.left - vpBCR.left
        const canvasTop = canvasBCR.top - vpBCR.top
        const dsx = canvasBCR.width / canvas.width
        const dsy = canvasBCR.height / canvas.height
        const x = cropRect.x * dsx + canvasLeft
        const y = cropRect.y * dsy + canvasTop
        const w = cropRect.w * dsx
        const h = cropRect.h * dsy
        ctx.fillStyle = "rgba(0,0,0,0.55)"
        ctx.fillRect(0, 0, vw, vh)
        ctx.clearRect(x, y, w, h)
        ctx.strokeStyle = "#00e5ff"
        ctx.lineWidth = 2
        ctx.strokeRect(x, y, w, h)
        const hs = 8
        ctx.fillStyle = "#00e5ff"
        const corners: [number, number][] = [[x, y], [x + w - hs, y], [x, y + h - hs], [x + w - hs, y + h - hs]]
        for (const [cx, cy] of corners) ctx.fillRect(cx, cy, hs, hs)
      } else {
        ctx.fillStyle = "rgba(0,0,0,0.35)"
        ctx.fillRect(0, 0, vw, vh)
        ctx.fillStyle = "#aaa"
        ctx.font = "14px sans-serif"
        ctx.textAlign = "center"
        ctx.fillText(passportConf.hintText, vw / 2, vh / 2)
      }
      return
    }

    if (regularEraserMode && regularCursorPos) {
      const canvas = canvasRef.current
      if (!canvas) return
      const canvasBCR = canvas.getBoundingClientRect()
      const vpBCR = viewport.getBoundingClientRect()
      const canvasLeft = canvasBCR.left - vpBCR.left
      const canvasTop = canvasBCR.top - vpBCR.top
      const canvasRight = canvasLeft + canvasBCR.width
      const canvasBottom = canvasTop + canvasBCR.height
      if (
        regularCursorPos.x >= canvasLeft && regularCursorPos.x <= canvasRight &&
        regularCursorPos.y >= canvasTop && regularCursorPos.y <= canvasBottom
      ) {
        const scaleX = canvas.width / canvasBCR.width
        const eraserRadius = Math.max(2, (regularEraserSize / 2) / Math.max(0.001, scaleX))
        ctx.lineWidth = 1.5
        ctx.setLineDash([4, 4])
        ctx.strokeStyle = "rgba(180,0,0,0.9)"
        ctx.beginPath()
        ctx.arc(regularCursorPos.x, regularCursorPos.y, eraserRadius, 0, Math.PI * 2)
        ctx.stroke()
        ctx.lineDashOffset = 4
        ctx.strokeStyle = "rgba(255,255,255,0.95)"
        ctx.beginPath()
        ctx.arc(regularCursorPos.x, regularCursorPos.y, eraserRadius, 0, Math.PI * 2)
        ctx.stroke()
        ctx.setLineDash([])
      }
      return
    }

    if (!magicEraserMode || !magicCursorPos) return
    const canvas = canvasRef.current
    if (!canvas) return
    const canvasBCR = canvas.getBoundingClientRect()
    const vpBCR = viewport.getBoundingClientRect()
    const canvasLeft = canvasBCR.left - vpBCR.left
    const canvasTop = canvasBCR.top - vpBCR.top
    const canvasRight = canvasLeft + canvasBCR.width
    const canvasBottom = canvasTop + canvasBCR.height
    if (
      magicCursorPos.x < canvasLeft || magicCursorPos.x > canvasRight ||
      magicCursorPos.y < canvasTop || magicCursorPos.y > canvasBottom
    ) return

    const scaleX = canvas.width / canvasBCR.width
    const sampleRadius = Math.max(0, Math.floor(magicSampleSize / 2))
    const radius = Math.max(2, sampleRadius / Math.max(0.001, scaleX))

    // Double-stroked dashed ring keeps the cursor visible on light and dark backgrounds.
    ctx.lineWidth = 1.5
    ctx.setLineDash([4, 4])
    ctx.strokeStyle = "rgba(0,0,0,0.8)"
    ctx.beginPath()
    ctx.arc(magicCursorPos.x, magicCursorPos.y, radius, 0, Math.PI * 2)
    ctx.stroke()
    ctx.lineDashOffset = 4
    ctx.strokeStyle = "rgba(255,255,255,0.95)"
    ctx.beginPath()
    ctx.arc(magicCursorPos.x, magicCursorPos.y, radius, 0, Math.PI * 2)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.fillStyle = "rgba(255,255,255,0.9)"
    ctx.fillRect(magicCursorPos.x - 1, magicCursorPos.y - 1, 2, 2)
  }, [cropMode, cropRect, passportConf, regularEraserMode, regularCursorPos, regularEraserSize, magicEraserMode, magicCursorPos, magicSampleSize, zoom, offset, croppedSrc, overlayLayoutVersion])

  // ── copy crop region ──────────────────────────────────────────────────────
  const handleCopy = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    // cropRect is stored in canvas-image pixel coords — use directly.
    const sx = cropRect ? Math.max(0, Math.round(cropRect.x)) : 0
    const sy = cropRect ? Math.max(0, Math.round(cropRect.y)) : 0
    const sw = cropRect ? Math.min(Math.round(cropRect.w), canvas.width - sx) : canvas.width
    const sh = cropRect ? Math.min(Math.round(cropRect.h), canvas.height - sy) : canvas.height
    if (sw <= 0 || sh <= 0) return
    const offscreen = document.createElement("canvas")
    offscreen.width = sw
    offscreen.height = sh
    const octx = offscreen.getContext("2d")
    if (!octx) return
    octx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)
    setCroppedSrc(offscreen.toDataURL('image/jpeg', 0.95))
  }

  // ── generate 4×6 print layout ─────────────────────────────────────────────
  //  Canvas: 4 in × 6 in @ 300 DPI  = 1200 × 1800 px
  const handleGenerate4x6 = () => {
    if (!croppedSrc) return
    const DPI = 300
    const MM_PER_INCH = 25.4
    const conf = passportConf
    const paperW = Math.round(4 * DPI)   // 1200 px
    const paperH = Math.round(6 * DPI)   // 1800 px

    const photoW = conf.photoWin != null
      ? Math.round(conf.photoWin * DPI)
      : Math.round((conf.photoWmm! / MM_PER_INCH) * DPI)
    const photoH = conf.photoHin != null
      ? Math.round(conf.photoHin * DPI)
      : Math.round((conf.photoHmm! / MM_PER_INCH) * DPI)

    const { cols, rows } = conf
    const gapX = Math.floor((paperW - cols * photoW) / (cols + 1))
    const gapY = Math.floor((paperH - rows * photoH) / (rows + 1))

    const paper = document.createElement("canvas")
    paper.width = paperW
    paper.height = paperH
    const pctx = paper.getContext("2d")
    if (!pctx) return

    pctx.fillStyle = "#ffffff"
    pctx.fillRect(0, 0, paperW, paperH)

    const img = new Image()
    img.onload = () => {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dx = gapX + c * (photoW + gapX)
          const dy = gapY + r * (photoH + gapY)
          pctx.drawImage(img, dx, dy, photoW, photoH)
        }
      }
      setPhoto4x6Src(paper.toDataURL("image/jpeg", 0.95))
    }
    img.src = croppedSrc
  }

  const handleDownloadCurrentImage = async () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.95)
    })
    if (!blob) {
      setBgRemovalError("Failed to prepare the image for download.")
      return
    }

    const file = new File([blob], "edited.jpg", { type: "image/jpeg" })

    try {
      if (
        isiOS &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: "edited.jpg",
        })
      } else {
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = objectUrl
        a.download = "edited.jpg"
        a.click()
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      }

      logEvent({
        event: "download",
        imageType: isiOS ? "edited_ios_share" : "edited",
        passportType,
        approxSizeKB: Math.round(blob.size / 1024),
        userAgent: navigator.userAgent,
      })
      setBgRemovalError(null)
    } catch (err: any) {
      if (err?.name === "AbortError") return
      setBgRemovalError(isiOS ? "Could not open the iPhone share sheet. Please try again." : "Failed to download the image.")
    }
  }

  const handleToggleCrop = () => {
    setCropMode((m) => {
      if (m) setCropRect(null) // clear rect on exit
      return !m
    })
  }

  const getCursor = () => {
    if (regularEraserMode) return "crosshair"
    if (magicEraserMode) return "crosshair"
    if (!cropMode) return isPanning.current ? "grabbing" : "grab"
    if (cropDragKind.current === "moving") return "move"
    return "crosshair"
  }

  // ── histogram computation (from original pixels) ─────────────────────────
  useEffect(() => {
    const img = originalImageRef.current
    if (!img) return
    const oc = document.createElement('canvas')
    oc.width = img.naturalWidth
    oc.height = img.naturalHeight
    const octx = oc.getContext('2d', { willReadFrequently: true })
    if (!octx) return
    octx.drawImage(img, 0, 0)
    const data = octx.getImageData(0, 0, oc.width, oc.height).data
    const rc = new Array(256).fill(0)
    const gc = new Array(256).fill(0)
    const bc = new Array(256).fill(0)
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue  // skip transparent (bg-removed) pixels
      rc[data[i]]++; gc[data[i + 1]]++; bc[data[i + 2]]++
    }
    const norm = (arr: number[]) => { const m = Math.max(...arr) || 1; return arr.map(v => v / m) }
    setHistData({ r: norm(rc), g: norm(gc), b: norm(bc) })
  }, [imageVersion])

  // ── histogram drawing ─────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = histRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#f8f8f8'
    ctx.fillRect(0, 0, W, H)
    const draw = (arr: number[], color: string, alpha: number) => {
      ctx.globalAlpha = alpha
      ctx.fillStyle = color
      for (let i = 0; i < 256; i++) {
        const h = Math.ceil(arr[i] * H)
        ctx.fillRect(i, H - h, 1, h)
      }
      ctx.globalAlpha = 1
    }
    if (channel === 'rgb') {
      draw(histData.r, '#e84040', 0.6)
      draw(histData.g, '#22a830', 0.6)
      draw(histData.b, '#1677ff', 0.6)
    } else if (channel === 'r') {
      draw(histData.r, '#e84040', 1)
    } else if (channel === 'g') {
      draw(histData.g, '#22a830', 1)
    } else {
      draw(histData.b, '#1677ff', 1)
    }
  }, [histData, channel])

  // ── custom levels slider drag ──────────────────────────────────────────────
  const startHandleDrag = (handle: 'black' | 'gamma' | 'white') =>
    (e: React.MouseEvent) => {
      e.preventDefault()
      const onMove = (ev: MouseEvent) => {
        const track = inputTrackRef.current
        if (!track) return
        const rect = track.getBoundingClientRect()
        const ratio = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
        const val = Math.round(ratio * 255)
        setLevels(l => {
          const upd = (lv: ChannelLevels): ChannelLevels => {
            if (handle === 'black') return { ...lv, inputBlack: Math.min(val, lv.inputWhite - 2) }
            if (handle === 'white') return { ...lv, inputWhite: Math.max(val, lv.inputBlack + 2) }
            if (handle === 'gamma') {
              const norm = (Math.max(lv.inputBlack + 1, Math.min(lv.inputWhite - 1, val)) - lv.inputBlack) / (lv.inputWhite - lv.inputBlack)
              const g = Math.max(0.1, Math.min(9.99, Math.log(0.5) / Math.log(Math.max(0.001, Math.min(0.999, norm)))))
              return { ...lv, gamma: parseFloat(g.toFixed(2)) }
            }
            return lv
          }
          if (channel === 'rgb') return { r: upd(l.r), g: upd(l.g), b: upd(l.b) }
          return { ...l, [channel]: upd(l[channel as 'r' | 'g' | 'b']) }
        })
      }
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

  const applyLevels = useCallback(() => {
    const canvas = canvasRef.current
    const img = originalImageRef.current
    if (!canvas || !img) return
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    // Use an off-screen canvas to read the original RGBA pixel data so that
    // alpha=0 pixels (background removed by AI) are not included in the levels
    // mapping and so their transparency is preserved for compositing.
    const offscreen = document.createElement('canvas')
    offscreen.width = canvas.width
    offscreen.height = canvas.height
    const offCtx = offscreen.getContext('2d', { willReadFrequently: true })
    if (!offCtx) return
    offCtx.drawImage(img, 0, 0)
    const imageData = offCtx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    const chs = [levels.r, levels.g, levels.b]
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue  // skip fully transparent (removed bg) pixels
      for (let c = 0; c < 3; c++) {
        const lv = chs[c]
        let val = data[i + c]
        val = Math.max(0, Math.min(255, ((val - lv.inputBlack) / (lv.inputWhite - lv.inputBlack)) * 255))
        val = 255 * Math.pow(val / 255, 1 / lv.gamma)
        data[i + c] = Math.max(0, Math.min(255, Math.round(val)))
      }
    }
    // Composite the levels-adjusted RGBA over a white background.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    offCtx.putImageData(imageData, 0, 0)
    ctx.drawImage(offscreen, 0, 0)
  }, [levels])

  const pushUndoState = useCallback((src: string) => {
    const hist = editHistoryRef.current
    if (hist.length >= MAX_UNDO_HISTORY) hist.shift()
    hist.push(src)
  }, [])

  const setWorkingImageFromDataUrl = (src: string, markBgRemoved: boolean = true) => {
    const img = new Image()
    img.onload = () => {
      originalImageRef.current = img
      setImageSrc(src)
      setBackgroundRemoved(markBgRemoved)
      setBgRemovalError(null)
      setCropRect(null)
      setCroppedSrc(null)
      setPhoto4x6Src(null)
      setImageVersion((v) => v + 1)
      applyLevels()
    }
    img.onerror = () => {
      setBgRemovalError("Failed to load processed image.")
    }
    img.src = src
  }

  const handleRestoreBackground = () => {
    if (!uploadedSrc || !uploadedImageRef.current || imageSrc === uploadedSrc) return
    if (imageSrc) pushUndoState(imageSrc)
    originalImageRef.current = uploadedImageRef.current
    setImageSrc(uploadedSrc)
    setBackgroundRemoved(false)
    setBgRemovalError(null)
    setCropRect(null)
    setCroppedSrc(null)
    setPhoto4x6Src(null)
    setImageVersion((v) => v + 1)
    applyLevels()
  }

  const handleUndoEdit = useCallback(() => {
    const hist = editHistoryRef.current
    if (hist.length === 0) return
    const prevSrc = hist.pop()
    if (!prevSrc) return
    setWorkingImageFromDataUrl(prevSrc, prevSrc !== uploadedSrc)
  }, [setWorkingImageFromDataUrl, uploadedSrc])

  useEffect(() => {
    if (!imageSrc) return
    const id = setTimeout(() => applyLevels(), 80)
    return () => clearTimeout(id)
  }, [levels, imageSrc, applyLevels])

  const handleAutoRemoveBackground = async () => {
    const canvas = canvasRef.current
    if (!canvas || !imageSrc || autoRemovingBg) return

    setBgRemovalError(null)
    setMagicEraserMode(false)
    setRegularEraserMode(false)
    setMagicCursorPos(null)
    setRegularCursorPos(null)
    setAutoRemovingBg(true)

    try {
      // Compress to JPEG and cap long edge at 1600 px before sending to the
      // background-removal service. This dramatically reduces upload size with
      // no meaningful loss in AI segmentation quality.
      const MAX_EDGE = 1600
      let srcCanvas = canvas
      if (canvas.width > MAX_EDGE || canvas.height > MAX_EDGE) {
        const scale = MAX_EDGE / Math.max(canvas.width, canvas.height)
        const offscreen = document.createElement("canvas")
        offscreen.width = Math.round(canvas.width * scale)
        offscreen.height = Math.round(canvas.height * scale)
        const octx = offscreen.getContext("2d")
        if (octx) {
          octx.drawImage(canvas, 0, 0, offscreen.width, offscreen.height)
          srcCanvas = offscreen
        }
      }
      const blob = await new Promise<Blob>((resolve, reject) => {
        srcCanvas.toBlob((b) => {
          if (b) resolve(b)
          else reject(new Error("Failed to prepare image for background removal."))
        }, "image/jpeg", 0.92)
      })

      const NGINX_LIMIT = 20 * 1024 * 1024 // 20 MB
      if (blob.size > NGINX_LIMIT) {
        const sizeMB = (blob.size / (1024 * 1024)).toFixed(1)
        throw new Error(
          `Image is ${sizeMB} MB and exceeds the upload limit. `
        )
      }

      const resultBlob = await removeBackgroundAI(blob, "passport-source.jpg")
      const resultDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error("Failed to read processed image."))
        reader.readAsDataURL(resultBlob)
      })

      if (imageSrc) pushUndoState(imageSrc)
      setWorkingImageFromDataUrl(resultDataUrl, true)
      logEvent({
        event: "auto_remove_background",
        source: "self_hosted",
        userAgent: navigator.userAgent,
      })
    } catch (err: any) {
      setBgRemovalError(err?.message || "Auto background removal failed.")
    } finally {
      setAutoRemovingBg(false)
    }
  }

  const handleImageUpload = (file: File) => {
    setUploadError(null)
    setBgRemovalError(null)
    setConverting(false)
    setBackgroundRemoved(false)
    setRegularEraserMode(false)
    setRegularCursorPos(null)
    editHistoryRef.current = []
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')

    const loadFromBlob = (blob: Blob) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const src = e.target?.result as string
        setImageSrc(src)
        setUploadedSrc(src)
        const img = new Image()
        img.onload = () => {
          originalImageRef.current = img
          uploadedImageRef.current = img
          handleResetView()
          setCropRect(null)
          setCroppedSrc(null)
          setPhoto4x6Src(null)
          setImageVersion(v => v + 1)
          applyLevels()
        }
        img.onerror = () => setUploadError('Failed to decode image.')
        img.src = src
      }
      reader.readAsDataURL(blob)
    }

    if (isHeic) {
      setConverting(true)
      const objectUrl = URL.createObjectURL(file)
      const testImg = new Image()
      testImg.onload = () => {
        // Browser natively decoded HEIC (Safari) — rasterise to JPEG via canvas
        const oc = document.createElement('canvas')
        oc.width = testImg.naturalWidth
        oc.height = testImg.naturalHeight
        oc.getContext('2d')!.drawImage(testImg, 0, 0)
        URL.revokeObjectURL(objectUrl)
        oc.toBlob((blob) => {
          setConverting(false)
          if (blob) loadFromBlob(blob)
          else setUploadError('Failed to rasterise HEIC image.')
        }, 'image/jpeg', 0.95)
      }
      testImg.onerror = () => {
        // Native decode failed — use libheif-js WASM fallback (Chrome)
        URL.revokeObjectURL(objectUrl)
        file.arrayBuffer().then(async (buf) => {
          const libheif = await import('libheif-js/wasm-bundle')
          const decoder = new libheif.HeifDecoder()
          const images = decoder.decode(new Uint8Array(buf))
          if (!images || images.length === 0) throw new Error('No images decoded from HEIC file')
          const img = images[0]
          const w = img.get_width()
          const h = img.get_height()
          const oc = document.createElement('canvas')
          oc.width = w
          oc.height = h
          const ctx = oc.getContext('2d')!
          const imageData = ctx.createImageData(w, h)
          await new Promise<void>((resolve, reject) => {
            img.display(imageData, (result: ImageData | null) => {
              if (result) resolve()
              else reject(new Error('libheif display() returned null'))
            })
          })
          ctx.putImageData(imageData, 0, 0)
          oc.toBlob((blob) => {
            setConverting(false)
            if (blob) loadFromBlob(blob)
            else setUploadError('Failed to encode decoded HEIC as JPEG.')
          }, 'image/jpeg', 0.95)
        }).catch((err) => {
          setConverting(false)
          console.error('HEIC conversion failed:', err)
          setUploadError(`HEIC conversion failed: ${err?.message ?? err}`)
        })
      }
      testImg.src = objectUrl
    } else {
      loadFromBlob(file)
    }
    return false
  }

  const handleReset = () => {
    handleResetView()
    handleRestoreBackground()
    setCropRect(null)
    setCroppedSrc(null)
    setPhoto4x6Src(null)
    setCropMode(false)
    setUploadError(null)
    setBgRemovalError(null)
    setConverting(false)
    setMagicEraserMode(false)
    setMagicTolerance(36)
    setMagicSampleSize(9)
    setRegularEraserMode(false)
    setRegularEraserSize(32)
    setMagicCursorPos(null)
    setRegularCursorPos(null)
    editHistoryRef.current = []
    setChannel('rgb')
    setLevels({ r: defaultCh(), g: defaultCh(), b: defaultCh() })
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z"
      if (!isUndo) return
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase()
      if (tag === "input" || tag === "textarea") return
      e.preventDefault()
      handleUndoEdit()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [handleUndoEdit])

  // keep a stable ref so the window drop handler always calls the latest version
  const handleImageUploadRef = useRef(handleImageUpload)
  useEffect(() => { handleImageUploadRef.current = handleImageUpload })

  // ── window-level drag-and-drop ─────────────────────────────────────────────
  useEffect(() => {
    const onDragOver = (e: DragEvent) => e.preventDefault()
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer?.files[0]
      const accepted = ['image/png','image/jpeg','image/tiff','image/heic','image/heif']
      if (file && (accepted.includes(file.type) || file.type === '')) {
        handleImageUploadRef.current(file)
      }
    }
    window.addEventListener("dragover", onDragOver)
    window.addEventListener("drop", onDrop)
    return () => {
      window.removeEventListener("dragover", onDragOver)
      window.removeEventListener("drop", onDrop)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    hello({ n: 1 })
      .then((response) => {
        if (isMounted) {
          setHelloMessage(response.message)
        }
      })
      .catch(() => {
        if (isMounted) {
          setHelloMessage("Hello message is unavailable.")
        }
      })

    return () => {
      isMounted = false
    }
  }, [])
  

  return (
    <PageLayout>

          <Introduction />

          {/* Passport type selector */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
            <Segmented
              size="large"
              value={passportType}
              onChange={(v) => {
                setPassportType(v as PassportType)
                setCropRect(null)
                setCroppedSrc(null)
                setPhoto4x6Src(null)
              }}
              options={[
                { label: PASSPORT_CONFIG.cn.label, value: 'cn' },
                { label: PASSPORT_CONFIG.us.label, value: 'us' },
              ]}
              style={{ fontWeight: 600, fontSize: 15 }}
            />
          </div>

          {/* Image Display Box */}
          <Card
            style={{
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
              border: "1px solid #e8e8e8",
              borderRadius: 12,
              marginBottom: 20,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            {!imageSrc ? (
              <div style={{ textAlign: "center", padding: isMobile ? "24px 16px" : "48px 32px" }}>
                <Upload accept="image/png,image/jpeg,image/tiff,image/heic,image/heif,.png,.jpg,.jpeg,.tiff,.tif,.heic,.heif" showUploadList={false} beforeUpload={handleImageUpload}>
                  <div style={{
                    textAlign: "center", cursor: "pointer",
                    border: "2px dashed #91caff", borderRadius: 12, padding: isMobile ? "24px 20px" : "40px 60px",
                    background: "#f0f7ff", transition: "border-color 0.2s",
                  }}>
                    <UploadOutlined style={{ fontSize: isMobile ? 38 : 52, marginBottom: 14, color: "#1677ff" }} />
                    <p style={{ color: "#1677ff", fontSize: 15, margin: 0, fontWeight: 500 }}>Click or drag an image here</p>
                    <p style={{ color: "#8ab4d4", fontSize: 12, margin: "6px 0 0" }}>PNG · JPG · TIFF · HEIC</p>
                  </div>
                </Upload>
                {uploadError && (
                  <div style={{ marginTop: 12, color: '#cf1322', fontSize: 13 }}>{uploadError}</div>
                )}
                {converting && (
                  <div style={{ marginTop: 12, color: '#555', fontSize: 13 }}>Converting HEIC…</div>
                )}
              </div>
            ) : (
              <div style={{ width: "100%" }}>
                {/* Source + cropped preview: side-by-side on desktop, stacked on mobile */}
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 24, alignItems: "flex-start", width: "100%" }}>

                  {/* ── Source image viewport ── */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      ref={viewportRef}
                      style={{
                        position: "relative",
                        overflow: "hidden",
                        width: "100%",
                        height: isMobile ? 300 : 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: getCursor(),
                        userSelect: "none",
                        background: "#e8e8e8",
                      }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseLeave}
                    >
                      {/* image canvas */}
                      <canvas
                        ref={canvasRef}
                        style={{
                          maxWidth: "100%",
                          maxHeight: isMobile ? 300 : 500,
                          objectFit: "contain",
                          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                          transformOrigin: "center center",
                          pointerEvents: "none",
                          display: "block",
                        }}
                      />
                      {/* crop overlay */}
                      <canvas
                        ref={overlayRef}
                        style={{
                          position: "absolute",
                          top: 0, left: 0,
                          width: "100%",
                          height: "100%",
                          pointerEvents: "none",
                        }}
                      />
                    </div>

                    {/* Controls */}
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 8 }}>
                      <Space wrap>
                        <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small">Zoom Out</Button>
                        <span style={{ color: "#555", minWidth: 48, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
                        <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small">Zoom In</Button>
                        <Button icon={<ExpandOutlined />} onClick={handleResetView} size="small">Reset View</Button>
                        <Button
                          icon={<ScissorOutlined />}
                          onClick={() => {
                            setMagicEraserMode(false)
                            setRegularEraserMode(false)
                            setRegularCursorPos(null)
                            handleToggleCrop()
                          }}
                          size="small"
                          type={cropMode ? "primary" : "default"}
                          danger={cropMode}
                        >
                          {cropMode ? "Exit Crop" : "Crop"}
                        </Button>
                        <Button
                          icon={<CopyOutlined />}
                          onClick={handleCopy}
                          size="small"
                          disabled={!imageSrc}
                        >
                          Generate
                        </Button>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={handleRestoreBackground}
                          size="small"
                          disabled={!backgroundRemoved}
                        >
                          Restore Original
                        </Button>
                        <Button
                          onClick={handleAutoRemoveBackground}
                          size="small"
                          type="primary"
                          loading={autoRemovingBg}
                          disabled={autoRemovingBg}
                        >
                          {autoRemovingBg ? "Removing..." : "Auto Remove Background"}
                        </Button>
                        <Button
                          onClick={handleDownloadCurrentImage}
                          size="small"
                          disabled={!imageSrc}
                        >
                          {isiOS ? "Save to Photos" : "Download JPEG"}
                        </Button>
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setCropMode(false)
                            setCropRect(null)
                            setRegularEraserMode(false)
                            setRegularCursorPos(null)
                            setMagicEraserMode((v) => !v)
                            setMagicCursorPos(null)
                            setBgRemovalError(null)
                          }}
                          size="small"
                          type={magicEraserMode ? "primary" : "default"}
                        >
                          {magicEraserMode ? "Magic Eraser On" : "Magic Eraser"}
                        </Button>
                        
                        <Button
                          onClick={() => {
                            setCropMode(false)
                            setCropRect(null)
                            setMagicEraserMode(false)
                            setMagicCursorPos(null)
                            setRegularEraserMode((v) => !v)
                            setRegularCursorPos(null)
                            setBgRemovalError(null)
                          }}
                          size="small"
                          type={regularEraserMode ? "primary" : "default"}
                        >
                          {regularEraserMode ? "Eraser On" : "Eraser"}
                        </Button>
                        <Upload accept="image/png,image/jpeg,image/tiff,image/heic,image/heif,.png,.jpg,.jpeg,.tiff,.tif,.heic,.heif" showUploadList={false} beforeUpload={handleImageUpload}>
                          <Button icon={<UploadOutlined />} size="small">Replace Image</Button>
                        </Upload>
                      </Space>
                    </div>
                    {regularEraserMode && (
                      <div style={{ marginTop: 8, padding: '0 10px' }}>
                        <div style={{ fontSize: 12, color: '#666', textAlign: 'center', marginBottom: 2 }}>
                          Eraser Size: {regularEraserSize}px
                        </div>
                        <Slider
                          min={4}
                          max={200}
                          step={1}
                          value={regularEraserSize}
                          onChange={(v) => setRegularEraserSize(v as number)}
                          tooltip={{ formatter: (v) => `${v}px` }}
                        />
                        <div style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: -4 }}>
                          Drag to erase all pixels inside the circle.
                        </div>
                      </div>
                    )}
                    {magicEraserMode && (
                      <div style={{ marginTop: 8, padding: '0 10px' }}>
                        <div style={{ fontSize: 12, color: '#666', textAlign: 'center', marginBottom: 2 }}>
                          Magic Eraser Sample Size: {magicSampleSize}px
                        </div>
                        <Slider
                          min={1}
                          max={41}
                          step={2}
                          value={magicSampleSize}
                          onChange={(v) => setMagicSampleSize(v as number)}
                          tooltip={{ formatter: (v) => `${v}px` }}
                        />
                        <div style={{ fontSize: 12, color: '#666', textAlign: 'center', marginBottom: 2 }}>
                          Magic Eraser Tolerance: {magicTolerance}
                        </div>
                        <Slider
                          min={8}
                          max={120}
                          step={1}
                          value={magicTolerance}
                          onChange={(v) => setMagicTolerance(v as number)}
                          tooltip={{ formatter: (v) => `Tolerance ${v}` }}
                        />
                        <div style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: -4 }}>
                          Click the image background to erase connected areas of similar color.
                        </div>
                      </div>
                    )}
                    {uploadError && (
                      <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12, textAlign: 'center' }}>{uploadError}</div>
                    )}
                    {bgRemovalError && (
                      <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12, textAlign: 'center' }}>{bgRemovalError}</div>
                    )}
                  </div>

                  {/* ── Cropped preview ── */}
                  {croppedSrc && (
                    <div style={{ flex: isMobile ? "1 1 auto" : "0 0 auto", width: isMobile ? "100%" : undefined, maxWidth: isMobile ? "100%" : 380, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ color: "#333", marginBottom: 8, fontWeight: 600 }}>Cropped Preview</span>
                      <div style={{ background: "#f5f5f5", border: "1px solid #ddd", padding: 8, borderRadius: 4 }}>
                        <img
                          src={croppedSrc}
                          alt="cropped"
                          style={{ maxWidth: 360, maxHeight: 520, display: "block", objectFit: "contain" }}
                        />
                      </div>
                      <Space orientation="vertical" style={{ marginTop: 8, width: "100%", alignItems: "center" }}>
                        <Button
                          size="small"
                          onClick={() => {
                            const a = document.createElement("a")
                            a.href = croppedSrc
                            a.download = "cropped.jpg"
                            a.click()
                            logEvent({
                              event: "download",
                              imageType: "single",
                              passportType,
                              approxSizeKB: Math.round(croppedSrc.length * 0.75 / 1024),
                              userAgent: navigator.userAgent,
                            })
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          onClick={handleGenerate4x6}
                        >
                          Make 4×6 photo paper
                        </Button>
                      </Space>

                      {/* 4×6 print layout preview */}
                      {photo4x6Src && (
                        <>
                          <span style={{ color: "#333", marginTop: 16, marginBottom: 8, fontWeight: 600 }}>4×6 Print Layout (300 DPI)</span>
                          <span style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>{passportConf.description}</span>
                          <div style={{ background: "#f5f5f5", border: "1px solid #ddd", padding: 8, borderRadius: 4 }}>
                            <img
                              src={photo4x6Src}
                              alt="4x6 print layout"
                              style={{ maxWidth: 360, maxHeight: 540, display: "block", objectFit: "contain" }}
                            />
                          </div>
                          <Button
                            size="small"
                            style={{ marginTop: 8 }}
                            onClick={() => {
                              const a = document.createElement("a")
                              a.href = photo4x6Src
                              a.download = "4x6_print.jpg"
                              a.click()
                              logEvent({
                                event: "download",
                                imageType: "4x6",
                                passportType,
                                approxSizeKB: Math.round(photo4x6Src.length * 0.75 / 1024),
                                userAgent: navigator.userAgent,
                              })
                            }}
                          >
                            Download 4×6
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* Level Adjustment Controls */}
          {imageSrc && (() => {
            const cur: ChannelLevels = channel === 'rgb' ? levels.r : levels[channel as 'r' | 'g' | 'b']
            const gammaPos = cur.inputBlack / 255 +
              (cur.inputWhite - cur.inputBlack) / 255 * Math.pow(0.5, 1 / cur.gamma)
            const handleStyle = (color: string, zIndex = 2): React.CSSProperties => ({
              position: 'absolute', top: 10, cursor: 'ew-resize', zIndex,
              width: 0, height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderBottom: `12px solid ${color}`,
              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))',
            })
            const chColor = { rgb: '#555', r: '#e84040', g: '#22a830', b: '#1677ff' }[channel]
            return (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#333' }}>Levels</span>
                    {/* Channel selector */}
                    <div style={{ display: 'flex', gap: 3 }}>
                      {(['rgb', 'r', 'g', 'b'] as Channel[]).map(ch => (
                        <button
                          key={ch}
                          onClick={() => setChannel(ch)}
                          style={{
                            padding: '1px 7px', border: '1px solid #d0d0d0', borderRadius: 3, cursor: 'pointer',
                            background: channel === ch ? chColor : '#f5f5f5',
                            color: channel === ch ? '#fff' : '#555',
                            fontWeight: channel === ch ? 700 : 400, fontSize: 11,
                          }}
                        >
                          {ch.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                }
                extra={<Button icon={<ReloadOutlined />} onClick={handleReset} size="small">Reset</Button>}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e8e8e8",
                  borderRadius: 12,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  width: 480,
                }}
                styles={{ body: { padding: '10px 14px 12px' } }}
              >
                {/* Histogram */}
                <canvas
                  ref={histRef}
                  width={256}
                  height={64}
                  style={{ width: '100%', height: 64, display: 'block', imageRendering: 'pixelated', borderBottom: '1px solid #e8e8e8', borderRadius: '4px 4px 0 0' }}
                />

                {/* Input three-handle slider */}
                <div style={{ position: 'relative', height: 28, marginTop: 2, userSelect: 'none' }}>
                  <div
                    ref={inputTrackRef}
                    style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 9,
                      background: 'linear-gradient(to right, #000, #fff)',
                      borderRadius: 2,
                    }}
                  />
                  <div onMouseDown={startHandleDrag('black')}
                    style={{ ...handleStyle('#111'), left: `calc(${cur.inputBlack / 255 * 100}% - 7px)`, borderBottomColor: '#222', outline: '1px solid #888' }} />
                  <div onMouseDown={startHandleDrag('gamma')}
                    style={{ ...handleStyle('#999', 3), left: `calc(${gammaPos * 100}% - 7px)` }} />
                  <div onMouseDown={startHandleDrag('white')}
                    style={{ ...handleStyle('#eee'), left: `calc(${cur.inputWhite / 255 * 100}% - 7px)`, outline: '1px solid #555' }} />
                </div>

                {/* Input numeric boxes */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  {([
                    { label: 'Min', field: 'inputBlack', val: cur.inputBlack, min: 0, max: 253, step: 1,
                      onChange: (v: number) => setLevels(l => { const upd = (lv: ChannelLevels) => ({ ...lv, inputBlack: Math.max(0, Math.min(v, lv.inputWhite - 2)) }); return channel === 'rgb' ? { r: upd(l.r), g: upd(l.g), b: upd(l.b) } : { ...l, [channel]: upd(l[channel as 'r'|'g'|'b']) } }) },
                    { label: 'γ',   field: 'gamma',      val: cur.gamma,      min: 0.1, max: 9.99, step: 0.01,
                      onChange: (v: number) => setLevels(l => { const upd = (lv: ChannelLevels) => ({ ...lv, gamma: Math.max(0.1, Math.min(9.99, v)) }); return channel === 'rgb' ? { r: upd(l.r), g: upd(l.g), b: upd(l.b) } : { ...l, [channel]: upd(l[channel as 'r'|'g'|'b']) } }) },
                    { label: 'Max', field: 'inputWhite', val: cur.inputWhite, min: 2, max: 255, step: 1,
                      onChange: (v: number) => setLevels(l => { const upd = (lv: ChannelLevels) => ({ ...lv, inputWhite: Math.max(lv.inputBlack + 2, Math.min(255, v)) }); return channel === 'rgb' ? { r: upd(l.r), g: upd(l.g), b: upd(l.b) } : { ...l, [channel]: upd(l[channel as 'r'|'g'|'b']) } }) },
                  ] as const).map(({ label, field, val, min, max, step, onChange }) => (
                    <div key={field} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <span style={{ color: '#aaa', fontSize: 10 }}>{label}</span>
                      <input
                        type="number" min={min} max={max} step={step}
                        value={field === 'gamma' ? (val as number).toFixed(2) : val}
                        onChange={e => { const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(n) }}
                        style={{
                          width: 52, textAlign: 'center', background: '#fafafa', color: '#333',
                          border: '1px solid #d0d0d0', borderRadius: 3, padding: '2px 4px', fontSize: 12,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Card>
              </div>
            )
          })()}

    </PageLayout>
  )
}

export default App
